import React, { useState, useEffect } from 'react';
import { ClipboardPaste, MousePointerSquareDashed, Eye, LayoutGrid, Loader2 } from 'lucide-react';
import { useCanvasStore } from '@/stores/canvasStore';
import { usePartnrBetaStore } from '@/stores/partnrBetaStore';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';

interface CanvasContextMenuProps {
  children: React.ReactNode;
  onPaste: (position: { x: number; y: number }) => void;
  onSelectAll: () => void;
  onRescueStrandedItems: () => void;
  getClickPosition: (e: React.MouseEvent) => { x: number; y: number };
}

export function CanvasContextMenu({ 
  children, 
  onPaste, 
  onSelectAll,
  onRescueStrandedItems,
  getClickPosition 
}: CanvasContextMenuProps) {
  const [clipboardHasContent, setClipboardHasContent] = useState(false);
  const [lastClickPosition, setLastClickPosition] = useState({ x: 0, y: 0 });
  const isPartnrBetaEnabled = usePartnrBetaStore((s) => s.isPartnrBetaEnabled);
  const viewMode = useCanvasStore((s) => s.viewMode);
  const currentBoardId = useCanvasStore((s) => s.currentBoardId);
  const isClusteringEnabled = useCanvasStore((s) => s.isClusteringEnabled);
  const isDetectingClusters = useCanvasStore((s) => s.isDetectingClusters);
  const clusterCount = useCanvasStore((s) => s.spatialClusters.length);
  // Cluster analysis lives here now; the right edge keeps only the Partnr launcher
  const showClusters = isPartnrBetaEnabled && viewMode === 'focus' && !!currentBoardId;

  useEffect(() => {
    const checkClipboard = async () => {
      try {
        // Check for text content
        const text = await navigator.clipboard.readText();
        setClipboardHasContent(!!text);
      } catch {
        // Clipboard access might be denied
        setClipboardHasContent(false);
      }
    };
    
    // Check clipboard when component mounts and on focus
    checkClipboard();
    window.addEventListener('focus', checkClipboard);
    return () => window.removeEventListener('focus', checkClipboard);
  }, []);

  const handleContextMenu = (e: React.MouseEvent) => {
    setLastClickPosition(getClickPosition(e));
  };

  const handlePaste = () => {
    onPaste(lastClickPosition);
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild onContextMenu={handleContextMenu}>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        <ContextMenuItem 
          onClick={handlePaste}
          disabled={!clipboardHasContent}
        >
          <ClipboardPaste className="mr-2 h-4 w-4" />
          <span>Paste</span>
          <span className="ml-auto text-xs text-muted-foreground">⌘V</span>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={onSelectAll}>
          <MousePointerSquareDashed className="mr-2 h-4 w-4" />
          <span>Select All</span>
          <span className="ml-auto text-xs text-muted-foreground">⌘A</span>
        </ContextMenuItem>
        {showClusters && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              disabled={isDetectingClusters}
              onClick={() => useCanvasStore.getState().setClusteringEnabled(!isClusteringEnabled)}
            >
              {isDetectingClusters ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
              ) : (
                <LayoutGrid className="mr-2 h-4 w-4" />
              )}
              <span>
                {isDetectingClusters ? 'Analyzing…' : isClusteringEnabled ? 'Hide clusters' : 'Analyze clusters'}
              </span>
              {isClusteringEnabled && clusterCount > 0 && (
                <span className="ml-auto text-xs text-muted-foreground">{clusterCount}</span>
              )}
            </ContextMenuItem>
          </>
        )}
        <ContextMenuSeparator />
        <ContextMenuItem onClick={onRescueStrandedItems}>
          <Eye className="mr-2 h-4 w-4" />
          <span>Rescue hidden items</span>
          <span className="ml-auto text-xs text-muted-foreground">⇧⌘R</span>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
